import React, { useState } from "react";

const ConfirmModal = ({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = "Confirmar",
  cancelText = "Cancelar",
  type = "warning", // warning, danger, success, info
  showComments = false,
  commentsLabel = "Comentarios",
  commentsPlaceholder = "Ingrese comentarios...",
}) => {
  const [comments, setComments] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  const typeConfig = {
    warning: {
      icon: "⚠️",
      bgColor: "bg-yellow-50",
      iconBg: "bg-yellow-100",
      iconColor: "text-yellow-600",
      confirmBg: "bg-yellow-600 hover:bg-yellow-700",
      borderColor: "border-yellow-200",
    },
    danger: {
      icon: "🚨",
      bgColor: "bg-red-50",
      iconBg: "bg-red-100",
      iconColor: "text-red-600",
      confirmBg: "bg-red-600 hover:bg-red-700",
      borderColor: "border-red-200",
    },
    success: {
      icon: "✅",
      bgColor: "bg-green-50",
      iconBg: "bg-green-100",
      iconColor: "text-green-600",
      confirmBg: "bg-green-600 hover:bg-green-700",
      borderColor: "border-green-200",
    },
    info: {
      icon: "ℹ️",
      bgColor: "bg-blue-50",
      iconBg: "bg-blue-100",
      iconColor: "text-blue-600",
      confirmBg: "bg-blue-600 hover:bg-blue-700",
      borderColor: "border-blue-200",
    },
  };

  const config = typeConfig[type] || typeConfig.warning;

  const handleConfirm = async () => {
    setIsLoading(true);
    try {
      await onConfirm(showComments ? comments : null);
      handleClose();
    } catch (error) {
      console.error("Error en confirmación:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setComments("");
    setIsLoading(false);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div
        className={`bg-white rounded-2xl shadow-2xl w-full max-w-md max-h-[90vh] overflow-y-auto border ${config.borderColor}`}
      >
        {/* Header */}
        <div className={`${config.bgColor} p-6 border-b ${config.borderColor}`}>
          <div className="flex items-center space-x-4">
            <div
              className={`w-12 h-12 ${config.iconBg} rounded-full flex items-center justify-center`}
            >
              <span className="text-2xl">{config.icon}</span>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{message}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6">
          {showComments && (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {commentsLabel}
              </label>
              <textarea
                value={comments}
                onChange={(e) => setComments(e.target.value)}
                rows="4"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 resize-none"
                placeholder={commentsPlaceholder}
              />
            </div>
          )}

          {/* Actions */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end space-y-3 sm:space-y-0 sm:space-x-3">
            <button
              onClick={handleClose}
              disabled={isLoading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors duration-200 disabled:opacity-50"
            >
              {cancelText}
            </button>
            <button
              onClick={handleConfirm}
              disabled={isLoading}
              className={`px-4 py-2 text-sm font-medium text-white ${config.confirmBg} rounded-lg transition-colors duration-200 disabled:opacity-50 flex items-center justify-center space-x-2`}
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                  <span>Procesando...</span>
                </>
              ) : (
                <span>{confirmText}</span>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConfirmModal;
